// ══════════════════════════════════════════════════════════════════════════════
// WORKPLACE FEED - Composant principal style Workplace by Meta
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, RefreshCw } from 'lucide-react';
import PostCard from './PostCard';
import PostComposer from './PostComposer';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WorkplaceFeed({ 
  contextType = 'team',
  contextId,
  token,
  currentUser,
  members = [],
  accentColor = '#E8650A',
  emptyStateMessage = 'Aucune publication',
  hideComposer = false,
  onComposerClick = null
}) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Construire l'URL de base selon le type de contexte
  const getBaseUrl = useCallback(() => {
    if (contextType === 'collab') {
      return `${API_URL}/api/collabs/${contextId}`;
    }
    return `${API_URL}/api/workplace/${contextType}s/${contextId}`;
  }, [contextId, contextType]);

  const fetchPosts = useCallback(async (showRefresh = false) => {
    if (!contextId) return;
    if (showRefresh) setRefreshing(true);
    
    // Posts de démo pour les groupes de démonstration
    const getDemoPosts = () => {
      const now = new Date();
      return [
        {
          id: 'demo-post-1',
          author_id: 'user-sophie',
          author_name: 'Sophie Martin',
          author_avatar: null,
          content: '🎉 Coucou la famille ! Qui est dispo pour le repas de dimanche ? Maman propose de faire son fameux poulet rôti !',
          post_type: 'text',
          media: [],
          reactions: { like: 3, love: 2 },
          my_reaction: null,
          comment_count: 4,
          created_at: new Date(now - 3600000 * 2).toISOString(),
          is_mine: false
        },
        {
          id: 'demo-post-2',
          author_id: 'user-lucas',
          author_name: 'Lucas Dupont',
          author_avatar: null,
          content: 'Qui veut participer au karaoké vendredi soir ? 🎤',
          post_type: 'poll',
          poll: {
            question: 'Qui veut participer au karaoké vendredi soir ? 🎤',
            options: [
              { id: 'opt-1', text: 'Moi, je viens !', vote_count: 4, voted: true },
              { id: 'opt-2', text: 'Peut-être, je confirme demain', vote_count: 2, voted: false },
              { id: 'opt-3', text: 'Pas dispo cette fois 😢', vote_count: 1, voted: false }
            ],
            total_votes: 7,
            multiple: false
          },
          reactions: { like: 5, haha: 2 },
          my_reaction: 'like',
          comment_count: 3,
          created_at: new Date(now - 3600000 * 5).toISOString(),
          is_mine: false
        },
        {
          id: 'demo-post-3',
          author_id: 'user-marie',
          author_name: 'Marie Dupont',
          author_avatar: null,
          content: "N'oubliez pas l'anniversaire de Papa !",
          post_type: 'event',
          event: {
            title: '🎂 Anniversaire Papa - 60 ans !',
            date: new Date(now.getTime() + 86400000 * 7).toISOString(),
            location: 'Restaurant Le Jardin - 12 rue des Lilas',
            responses: {
              going: ['user-sophie', 'user-lucas', 'current-user'],
              maybe: ['user-thomas'],
              not_going: []
            },
            my_response: 'going',
            going_count: 3,
            maybe_count: 1
          },
          reactions: { love: 6 },
          my_reaction: 'love',
          comment_count: 8,
          created_at: new Date(now - 3600000 * 24).toISOString(),
          is_mine: false
        },
        {
          id: 'demo-post-4',
          author_id: 'current-user',
          author_name: 'Moi',
          author_avatar: null,
          content: 'Qui peut récupérer les clés chez mamie demain ?',
          post_type: 'task',
          task: {
            title: 'Récupérer les clés chez Mamie',
            description: 'Elle part en vacances samedi, il faut récupérer les clés avant vendredi soir.',
            due_date: new Date(now.getTime() + 86400000 * 2).toISOString(),
            priority: 'high',
            assignees: [
              { id: 'user-lucas', name: 'Lucas' }
            ],
            status: 'pending'
          },
          reactions: { like: 1 },
          my_reaction: null,
          comment_count: 2,
          created_at: new Date(now - 3600000 * 8).toISOString(),
          is_mine: true
        },
        {
          id: 'demo-post-5',
          author_id: 'user-thomas',
          author_name: 'Thomas Dupont',
          author_avatar: null,
          content: 'Voici les photos du barbecue de la semaine dernière ! 📸 On a passé un super moment ensemble.',
          post_type: 'text',
          media: [
            { type: 'image', url: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=400', preview: null },
            { type: 'image', url: 'https://images.unsplash.com/photo-1529193591184-b1d58069ecdd?w=400', preview: null }
          ],
          reactions: { love: 8, like: 4, haha: 1 },
          my_reaction: 'love',
          comment_count: 6,
          created_at: new Date(now - 3600000 * 48).toISOString(),
          is_mine: false
        }
      ];
    };
    
    try {
      // Si c'est un groupe de démo, utiliser les posts de démo
      if (contextId.startsWith('demo-')) {
        setPosts(getDemoPosts());
        setLoading(false);
        setRefreshing(false);
        return;
      }
      
      const endpoint = `${getBaseUrl()}/feed`;
      const res = await fetch(endpoint, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
      } else {
        // Fallback vers posts de démo si l'API échoue
        setPosts(getDemoPosts());
      }
    } catch (error) {
      console.error('Erreur feed:', error);
      // Fallback vers posts de démo
      setPosts(getDemoPosts());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [contextId, getBaseUrl, token]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleCreatePost = async (postData) => {
    try {
      const endpoint = `${getBaseUrl()}/feed`;

      // Les Collabs n'ont pas de route with-media pour l'instant, on utilise JSON
      if (contextType === 'collab') {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            content: postData.content,
            post_type: postData.type || 'text',
            poll: postData.poll,
            event: postData.event,
            task: postData.task,
            mentions: []
          })
        });

        if (res.ok) {
          toast.success('Publication créée !');
          fetchPosts();
          return true;
        }
        return false;
      }

      // Pour Teams/Groups - Si fichiers média ou documents présents
      if ((postData.files && postData.files.length > 0) || (postData.documents && postData.documents.length > 0)) {
        const formData = new FormData();
        formData.append('content', postData.content || '');
        formData.append('post_type', postData.type || 'text');
        
        if (postData.poll) {
          formData.append('poll_question', postData.poll.question);
          formData.append('poll_options', JSON.stringify(postData.poll.options));
          formData.append('poll_multiple', postData.poll.multiple || false);
        }
        
        if (postData.event) {
          formData.append('event_title', postData.event.title);
          formData.append('event_date', postData.event.date || '');
          formData.append('event_location', postData.event.location || '');
        }
        
        if (postData.task) {
          formData.append('task_title', postData.task.title);
          formData.append('task_description', postData.task.description || '');
          formData.append('task_due_date', postData.task.due_date || '');
          formData.append('task_priority', postData.task.priority || 'medium');
          formData.append('task_assignees', JSON.stringify(postData.task.assignees || []));
        }
        
        if (postData.files) {
          postData.files.forEach(file => formData.append('files', file));
        }
        
        if (postData.documents) {
          postData.documents.forEach(doc => formData.append('documents', doc));
        }

        const res = await fetch(`${endpoint}/with-media`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (res.ok) {
          toast.success('Publication créée !');
          fetchPosts();
          return true;
        }
      } else {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            content: postData.content,
            post_type: postData.type || 'text',
            poll: postData.poll,
            event: postData.event,
            task: postData.task,
            mentions: []
          })
        });

        if (res.ok) {
          toast.success('Publication créée !');
          fetchPosts();
          return true;
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
    return false;
  };

  const handleReaction = async (postId, reactionType) => {
    try {
      const res = await fetch(`${getBaseUrl()}/feed/${postId}/react`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reaction: reactionType })
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(prev => prev.map(p => p.id === postId ? { ...p, reactions: data.reactions, my_reaction: data.my_reaction } : p));
      }
    } catch (error) {
      console.error('Erreur réaction:', error);
    }
  };

  const handleComment = async (postId, content) => {
    try {
      const res = await fetch(`${getBaseUrl()}/feed/${postId}/comments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ content })
      });
      if (res.ok) {
        fetchPosts();
        return true;
      }
    } catch (error) {
      console.error('Erreur commentaire:', error);
    }
    return false;
  };

  const loadComments = async (postId) => {
    try {
      const res = await fetch(`${getBaseUrl()}/feed/${postId}/comments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        return data.comments || [];
      }
    } catch (error) {
      console.error('Erreur commentaires:', error);
    }
    return [];
  };

  const handlePollVote = async (postId, optionIds) => {
    try {
      await fetch(`${getBaseUrl()}/feed/${postId}/poll/vote`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ option_ids: optionIds })
      });
      fetchPosts();
    } catch (error) {
      console.error('Erreur vote:', error);
    }
  };

  const handleEventResponse = async (postId, response) => {
    try {
      await fetch(`${getBaseUrl()}/feed/${postId}/event/respond`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ response })
      });
      fetchPosts();
      toast.success(response === 'going' ? 'Tu participes !' : response === 'maybe' ? 'Peut-être...' : 'Tu ne participeras pas');
    } catch (error) {
      console.error('Erreur événement:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm('Supprimer cette publication ?')) return;
    try {
      const res = await fetch(`${getBaseUrl()}/feed/${postId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Publication supprimée');
        setPosts(prev => prev.filter(p => p.id !== postId));
      }
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  const handleTaskStatusChange = async (postId, newStatus) => {
    try {
      const res = await fetch(`${getBaseUrl()}/feed/${postId}/task/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(prev => prev.map(p => 
          p.id === postId 
            ? { ...p, task: { ...p.task, status: newStatus } }
            : p
        ));
        toast.success(newStatus === 'completed' ? 'Tâche terminée !' : 'Statut mis à jour');
      }
    } catch (error) {
      toast.error('Erreur mise à jour tâche');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: accentColor }} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!hideComposer && (
        <PostComposer onSubmit={handleCreatePost} members={members} currentUser={currentUser} accentColor={accentColor} />
      )}

      <AnimatePresence>
        {posts.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accentColor}15` }}>
              <svg className="w-8 h-8" style={{ color: accentColor }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{emptyStateMessage}</h3>
            <p className="text-gray-500 text-sm">Sois le premier à partager quelque chose !</p>
          </motion.div>
        ) : (
          posts.map((post, i) => (
            <motion.div key={post.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <PostCard
                post={post}
                currentUser={currentUser}
                members={members}
                accentColor={accentColor}
                onReaction={handleReaction}
                onComment={handleComment}
                onLoadComments={loadComments}
                onPollVote={handlePollVote}
                onEventResponse={handleEventResponse}
                onDelete={handleDeletePost}
                onTaskStatusChange={handleTaskStatusChange}
              />
            </motion.div>
          ))
        )}
      </AnimatePresence>
    </div>
  );
}
